// frontend/src/lib/paidExposureEngine.js

/**
 * Paid Reports Exposure Engine (Assessment → Money Layer) — MVP hardened
 *
 * Goals:
 * - Deterministic, defensible exposure numbers
 * - Multi-regime output (EU/UK/US_FED/US_STATE)
 * - Must NEVER throw (returns a best-effort object)
 *
 * FX POLICY (MVP):
 * - Revenue inputs are in StartAudit reporting currency (cfg/summary currency_code)
 * - For percent-of-revenue ceilings, revenue is converted into the regime currency when FX is available
 * - If FX is missing, we fall back to treating input revenue as "native" with an explicit fx_status
 */

import { convertCurrencyUsingFx } from "./fxRates";

function n(x, fallback = 0) {
  const v = Number(x);
  return Number.isFinite(v) ? v : fallback;
}

function clamp(x, lo, hi) {
  const v = n(x, lo);
  return Math.max(lo, Math.min(hi, v));
}

function upper(s) {
  return String(s || "").trim().toUpperCase();
}

function uniq(arr) {
  const out = [];
  const seen = new Set();
  (Array.isArray(arr) ? arr : []).forEach((x) => {
    const k = upper(x);
    if (!k || seen.has(k)) return;
    seen.add(k);
    out.push(k);
  });
  return out;
}

function nowIso() {
  try {
    return new Date().toISOString();
  } catch {
    return "—";
  }
}

/**
 * Severity tier 1..5 -> multiplier (LOCKED contract).
 */
function severityMultiplier(tier) {
  const t = clamp(tier, 1, 5);
  return [0, 0.8, 0.95, 1.1, 1.28, 1.5][t] || 1.1;
}

/**
 * Compliance multiplier (LOCKED contract):
 * 1.0 + (1 - score/100) * 0.45
 */
function complianceMultiplier(scorePct) {
  const s = clamp(scorePct, 0, 100);
  return 1.0 + (1 - s / 100) * 0.45;
}

/**
 * Regime modeling factors (LOCKED contract).
 */
function regimeModelFactor(regime) {
  const r = upper(regime);
  if (r === "US_FED") return 1.12;
  if (r === "US_STATE") return 0.94;
  return 1.0;
}

/**
 * Inferred Federal Overlay (heuristic).
 */
function inferFederalOverlayFromConfig(cfg, summary) {
  const textFields = [];
  const candidates = [
    cfg?.industry,
    cfg?.sector,
    cfg?.niche,
    cfg?.businessType,
    cfg?.companyDescription,
    cfg?.description,
    cfg?.useCase,
    cfg?.use_case,
    cfg?.vertical,
    cfg?.naics,
    cfg?.sic,
    summary?.industry,
    summary?.sector,
  ];

  candidates.forEach((v) => {
    if (typeof v === "string" && v.trim()) textFields.push(v.trim());
    else if (Array.isArray(v))
      v.forEach((x) => (typeof x === "string" && x.trim() ? textFields.push(x.trim()) : null));
  });

  const haystack = textFields.join(" | ").toLowerCase();
  if (!haystack) return { inferred: false, reasons: [] };

  const rules = [
    {
      key: "HIPAA / healthcare",
      hits: ["hipaa", "hhs", "ocr", "phi", "healthcare", "medical", "clinic", "hospital", "patient", "claims"],
    },
    {
      key: "financial / securities",
      hits: ["sec", "finra", "broker", "investment", "securities", "advisor", "ria", "bank", "lending", "cfpb", "glba"],
    },
    {
      key: "telecom / communications",
      hits: ["fcc", "telecom", "carrier", "sms", "robocall", "autodial", "tcpa", "communications"],
    },
    {
      key: "consumer / advertising",
      hits: ["ftc", "advertising", "marketing", "consumer", "dark pattern", "unfair", "deceptive"],
    },
    { key: "education / minors", hits: ["ferpa", "student records", "school", "education"] },
    { key: "employment / workplace", hits: ["eeoc", "employment", "workplace", "hiring", "hr"] },
  ];

  const reasons = [];
  rules.forEach((r) => {
    const matched = r.hits.some((h) => haystack.includes(String(h).toLowerCase()));
    if (matched) reasons.push(r.key);
  });

  return { inferred: reasons.length > 0, reasons };
}

function inferSelectedRegimes(jurisdictions) {
  const j = uniq(jurisdictions);
  const mapped = new Set();
  j.forEach((x) => {
    if (x === "US") mapped.add("US_FED");
    else mapped.add(x);
  });
  return Array.from(mapped);
}

function currencyForRegime(regime, defaultCurrency) {
  const r = upper(regime);
  if (r === "UK") return { code: "GBP", symbol: "£" };
  if (r === "EU") return { code: "EUR", symbol: "€" };
  if (r === "US_FED" || r === "US_STATE") return { code: "USD", symbol: "$" };
  return {
    code: defaultCurrency?.code || "USD",
    symbol: defaultCurrency?.symbol || "$",
  };
}

/**
 * US State exposure planning model (Phase 1).
 *
 * IMPORTANT:
 * - `planning_ceiling_usd` is a deterministic planning ceiling used by AI Fine Protector.
 * - It is NOT presented as a legal opinion or guaranteed statutory maximum.
 * - Many state privacy laws use per-violation penalties rather than aggregate caps.
 */
const US_STATE_FALLBACK_DISCLOSURE =
  "State-specific statutory exposure data is not currently configured for this jurisdiction. A conservative planning estimate has been used for modelling purposes. This figure should not be interpreted as a statutory maximum or legal opinion.";

const US_STATE_DEFAULT_CEILING_USD = 50_000;

const US_STATE_EXPOSURE_MODELS = {
  CA: {
    planning_ceiling_usd: 2_500_000,
    legal_basis: "California CCPA/CPRA enforcement model; civil penalties can be assessed per violation, with higher penalties for intentional violations or violations involving minors.",
    statutory_penalty_basis: "Up to approximately $7,988 per intentional/minor violation, adjusted periodically.",
    confidence_level: "HIGH",
    source_label: "California Privacy Protection Agency / CCPA-CPRA penalty adjustment materials",
  },
  CO: {
    planning_ceiling_usd: 2_000_000,
    legal_basis: "Colorado Privacy Act enforced through Colorado consumer protection enforcement authority.",
    statutory_penalty_basis: "Up to $20,000 per violation under applicable Colorado consumer protection penalty authority.",
    confidence_level: "HIGH",
    source_label: "Colorado Privacy Act / Colorado Consumer Protection Act enforcement framework",
  },
  NY: {
    planning_ceiling_usd: 1_500_000,
    legal_basis: "New York SHIELD Act and breach-notice/safeguards enforcement framework.",
    statutory_penalty_basis: "Safeguards violations may be assessed up to $5,000 per violation; breach-notice penalties may be capped separately depending on violation type.",
    confidence_level: "MEDIUM_HIGH",
    source_label: "New York SHIELD Act / New York Attorney General enforcement materials",
  },
  TX: {
    planning_ceiling_usd: 5_000_000,
    legal_basis: "Texas Data Privacy and Security Act enforcement by the Texas Attorney General.",
    statutory_penalty_basis: "Up to $7,500 per violation after applicable notice/cure process.",
    confidence_level: "HIGH",
    source_label: "Texas Data Privacy and Security Act",
  },
  FL: {
    planning_ceiling_usd: 2_500_000,
    legal_basis: "Florida Digital Bill of Rights enforcement framework.",
    statutory_penalty_basis: "Up to $50,000 per violation, with potential enhanced penalties for certain violations involving children.",
    confidence_level: "HIGH",
    source_label: "Florida Digital Bill of Rights",
  },
  IL: {
    planning_ceiling_usd: 1_000_000,
    legal_basis: "Illinois biometric privacy and consumer privacy exposure framework.",
    statutory_penalty_basis: "BIPA liquidated damages historically include $1,000 negligent / $5,000 intentional or reckless violation figures, subject to statutory amendments and case-specific treatment.",
    confidence_level: "MEDIUM_HIGH",
    source_label: "Illinois Biometric Information Privacy Act and related amendments",
  },
  WA: {
    planning_ceiling_usd: 750_000,
    legal_basis: "Washington My Health My Data Act / Consumer Protection Act enforcement framework.",
    statutory_penalty_basis: "Consumer Protection Act penalties can reach up to $7,500 per violation in relevant enforcement contexts.",
    confidence_level: "MEDIUM_HIGH",
    source_label: "Washington My Health My Data Act / Consumer Protection Act",
  },
  MA: {
    planning_ceiling_usd: 750_000,
    legal_basis: "Massachusetts data security and consumer protection enforcement framework.",
    statutory_penalty_basis: "Common penalty anchors include $5,000 per violation and higher per-instance improper-disposal exposure depending on claim type.",
    confidence_level: "MEDIUM",
    source_label: "Massachusetts data security / consumer protection enforcement framework",
  },
  CT: {
    planning_ceiling_usd: 750_000,
    legal_basis: "Connecticut Data Privacy Act enforced through Connecticut unfair trade practices authority.",
    statutory_penalty_basis: "Up to $5,000 per willful violation under applicable CUTPA penalty authority.",
    confidence_level: "HIGH",
    source_label: "Connecticut Data Privacy Act / CUTPA enforcement framework",
  },
  VA: {
    planning_ceiling_usd: 750_000,
    legal_basis: "Virginia Consumer Data Protection Act enforcement by the Virginia Attorney General.",
    statutory_penalty_basis: "Up to $7,500 per violation.",
    confidence_level: "HIGH",
    source_label: "Virginia Consumer Data Protection Act",
  },
  OR: {
    planning_ceiling_usd: 750_000,
    legal_basis: "Oregon Consumer Privacy Act enforcement by the Oregon Attorney General.",
    statutory_penalty_basis: "Up to $7,500 per violation.",
    confidence_level: "HIGH",
    source_label: "Oregon Consumer Privacy Act",
  },
  DE: {
    planning_ceiling_usd: 1_000_000,
    legal_basis: "Delaware Personal Data Privacy Act enforced through Delaware consumer protection authority.",
    statutory_penalty_basis: "Up to $10,000 per violation.",
    confidence_level: "HIGH",
    source_label: "Delaware Personal Data Privacy Act",
  },
  NJ: {
    planning_ceiling_usd: 2_000_000,
    legal_basis: "New Jersey Data Privacy Act enforced through New Jersey consumer protection authority.",
    statutory_penalty_basis: "Up to $10,000 for an initial offense and $20,000 for later offenses.",
    confidence_level: "HIGH",
    source_label: "New Jersey Data Privacy Act / Consumer Fraud Act enforcement framework",
  },
  NH: {
    planning_ceiling_usd: 1_000_000,
    legal_basis: "New Hampshire Data Privacy Act enforced through deceptive trade practices authority.",
    statutory_penalty_basis: "Up to $10,000 per violation in civil penalties.",
    confidence_level: "HIGH",
    source_label: "New Hampshire Data Privacy Act / Consumer Protection Act framework",
  },
  IN: {
    planning_ceiling_usd: 750_000,
    legal_basis: "Indiana Consumer Data Protection Act enforcement by the Indiana Attorney General.",
    statutory_penalty_basis: "Up to $7,500 per violation after applicable cure process.",
    confidence_level: "HIGH",
    source_label: "Indiana Consumer Data Protection Act",
  },
  KY: {
    planning_ceiling_usd: 750_000,
    legal_basis: "Kentucky Consumer Data Protection Act enforcement by the Kentucky Attorney General.",
    statutory_penalty_basis: "Up to $7,500 per violation after applicable cure process.",
    confidence_level: "HIGH",
    source_label: "Kentucky Consumer Data Protection Act",
  },
  RI: {
    planning_ceiling_usd: 1_000_000,
    legal_basis: "Rhode Island Data Transparency and Privacy Protection Act enforcement framework.",
    statutory_penalty_basis: "Up to $10,000 per violation, with separate lower fines for certain intentional disclosures.",
    confidence_level: "HIGH",
    source_label: "Rhode Island Data Transparency and Privacy Protection Act",
  },
  MT: {
    planning_ceiling_usd: 750_000,
    legal_basis: "Montana Consumer Data Privacy Act enforcement framework, as amended.",
    statutory_penalty_basis: "Up to $7,500 per violation under amended Montana enforcement materials.",
    confidence_level: "MEDIUM_HIGH",
    source_label: "Montana Consumer Data Privacy Act / Montana Attorney General consumer privacy materials",
  },
  IA: {
    planning_ceiling_usd: 750_000,
    legal_basis: "Iowa Consumer Data Protection Act enforcement by the Iowa Attorney General.",
    statutory_penalty_basis: "Up to $7,500 per violation after applicable cure process.",
    confidence_level: "HIGH",
    source_label: "Iowa Consumer Data Protection Act",
  },
  NE: {
    planning_ceiling_usd: 750_000,
    legal_basis: "Nebraska Data Privacy Act enforcement by the Nebraska Attorney General.",
    statutory_penalty_basis: "Up to $7,500 per violation after applicable cure process.",
    confidence_level: "HIGH",
    source_label: "Nebraska Data Privacy Act",
  },
  UT: {
    planning_ceiling_usd: 750_000,
    legal_basis: "Utah Consumer Privacy Act enforcement framework.",
    statutory_penalty_basis: "State-specific model retained from prior implementation.",
    confidence_level: "MEDIUM",
    source_label: "Utah Consumer Privacy Act",
  },
};

function usStateWeightForCeiling(ceilingUsd) {
  const c = Math.max(0, n(ceilingUsd, 0));
  if (c >= 2_500_000) return 1.0;
  if (c >= 750_000) return 0.7;
  return 0.25;
}

function normalizeStateKey(x) {
  const s = upper(x);
  if (!s) return "";
  const m = s.match(/^US[_-]([A-Z]{2})$/);
  if (m && m[1]) return m[1];
  if (/^[A-Z]{2}$/.test(s)) return s;
  return s;
}

function resolveUsStateModel(stateCodeOrId) {
  const key = normalizeStateKey(stateCodeOrId);
  const configured = key ? US_STATE_EXPOSURE_MODELS[key] : null;

  if (configured) {
    return {
      state: key,
      planning_ceiling_usd: configured.planning_ceiling_usd,
      legal_basis: configured.legal_basis,
      statutory_penalty_basis: configured.statutory_penalty_basis,
      confidence_level: configured.confidence_level || "MEDIUM",
      source_label: configured.source_label || "State-specific planning model",
      fallback_state: false,
      fallback_disclosure: null,
    };
  }

  return {
    state: key || "(BASELINE)",
    planning_ceiling_usd: US_STATE_DEFAULT_CEILING_USD,
    legal_basis: "State-specific statutory exposure data is not currently configured for this jurisdiction.",
    statutory_penalty_basis: "Conservative planning fallback used pending state-specific configuration.",
    confidence_level: "LOW",
    source_label: "AI Fine Protector fallback planning model",
    fallback_state: true,
    fallback_disclosure: US_STATE_FALLBACK_DISCLOSURE,
  };
}

function resolveUsStateCeilingUsd(stateCodeOrId) {
  return resolveUsStateModel(stateCodeOrId).planning_ceiling_usd;
}

function statutoryCeiling(regime, revenueNative) {
  const rev = Math.max(0, n(revenueNative, 0));
  const r = upper(regime);

  if (r === "UK") return Math.max(17_500_000, 0.04 * rev);
  if (r === "EU") return Math.max(20_000_000, 0.04 * rev);
  if (r === "US_FED") return Math.max(10_000_000, 0.02 * rev);

  return Math.max(5_000_000, 0.01 * rev);
}

function baseRiskAmount(revenueNative, peoplePerMonth) {
  const rev = Math.max(0, n(revenueNative, 0));
  const ppl = Math.max(0, n(peoplePerMonth, 0));
  return 0.006 * rev + 12 * ppl;
}

function convertRevenueForRegime({ revenueInput, inputCurrency, regimeCurrency, fx }) {
  const from = upper(inputCurrency || "USD");
  const to = upper(regimeCurrency || from);

  if (from === to) return { revenueNative: revenueInput, fx_status: "NOT_REQUIRED" };

  const converted = convertCurrencyUsingFx({
    amount: revenueInput,
    from,
    to,
    fx,
  });

  if (typeof converted === "number" && Number.isFinite(converted)) {
    return { revenueNative: converted, fx_status: "OK" };
  }

  return { revenueNative: revenueInput, fx_status: "FAILED_FALLBACK_INPUT_AS_NATIVE" };
}

function computeRegimeExposure({
  regime,
  revenueInput,
  inputCurrency,
  regimeCurrency,
  peoplePerMonth,
  complianceScorePct,
  severityTier,
  fx,
}) {
  const r = upper(regime);

  const { revenueNative, fx_status } = convertRevenueForRegime({
    revenueInput,
    inputCurrency,
    regimeCurrency,
    fx,
  });

  const ceiling = statutoryCeiling(r, revenueNative);
  const base = baseRiskAmount(revenueNative, peoplePerMonth);
  const sevM = severityMultiplier(severityTier);
  const compM = complianceMultiplier(complianceScorePct);
  const modelF = regimeModelFactor(r);

  const structuredWorst = Math.min(ceiling, base * sevM * compM * modelF);
  const net = structuredWorst * 0.88;
  const floor = structuredWorst * 0.65;

  return {
    regime: r,
    enabled: true,
    statutory_ceiling_native: Math.round(ceiling),
    structured_worst_case_native: Math.round(structuredWorst),
    net_native: Math.round(net),
    floor_native: Math.round(floor),
    model_factor: modelF,
    fx_status,
  };
}

function pickUsStateAggregationMode(cfg) {
  const raw =
    cfg?.usStateAggregationMode ||
    cfg?.us_state_aggregation_mode ||
    cfg?.us_state_aggregation ||
    cfg?.usStateAggregation ||
    cfg?.stateAggregationMode ||
    "";

  const m = upper(raw);
  if (m === "SUM") return "SUM";
  if (m === "WEIGHTED" || m === "WEIGHT") return "WEIGHTED";
  return "MAX";
}

function aggregateUsState(per_state, mode) {
  const m = upper(mode || "MAX");
  const rows = Array.isArray(per_state) ? per_state : [];

  if (!rows.length) {
    return { statutory_ceiling_native: 0, structured_worst_case_native: 0, net_native: 0, floor_native: 0 };
  }

  if (m === "SUM") {
    return {
      statutory_ceiling_native: Math.round(rows.reduce((a, r) => a + n(r.ceiling_native, 0), 0)),
      structured_worst_case_native: Math.round(rows.reduce((a, r) => a + n(r.worst_native, 0), 0)),
      net_native: Math.round(rows.reduce((a, r) => a + n(r.net_native, 0), 0)),
      floor_native: Math.round(rows.reduce((a, r) => a + n(r.floor_native, 0), 0)),
    };
  }

  if (m === "WEIGHTED") {
    return {
      statutory_ceiling_native: Math.round(rows.reduce((a, r) => a + n(r.ceiling_native, 0) * n(r.weight, 0), 0)),
      structured_worst_case_native: Math.round(rows.reduce((a, r) => a + n(r.worst_native, 0) * n(r.weight, 0), 0)),
      net_native: Math.round(rows.reduce((a, r) => a + n(r.net_native, 0) * n(r.weight, 0), 0)),
      floor_native: Math.round(rows.reduce((a, r) => a + n(r.floor_native, 0) * n(r.weight, 0), 0)),
    };
  }

  // MAX default
  const maxBy = (key) =>
    rows.reduce((best, r) => (n(r[key], -Infinity) > n(best?.[key], -Infinity) ? r : best), rows[0]);

  const maxCeil = maxBy("ceiling_native");
  const maxWorst = maxBy("worst_native");
  const maxNet = maxBy("net_native");
  const maxFloor = maxBy("floor_native");

  return {
    statutory_ceiling_native: Math.round(n(maxCeil?.ceiling_native, 0)),
    structured_worst_case_native: Math.round(n(maxWorst?.worst_native, 0)),
    net_native: Math.round(n(maxNet?.net_native, 0)),
    floor_native: Math.round(n(maxFloor?.floor_native, 0)),
  };
}

/**
 * Public API (MUST NOT THROW):
 * computePaidReportExposure({ configForEngine, canonicalPayload })
 */
export function computePaidReportExposure({ configForEngine, canonicalPayload }) {
  try {
    const cfg = configForEngine || canonicalPayload?.config || {};
    const assessment = canonicalPayload?.assessment || {};
    const summary = canonicalPayload?.summary || {};

    const selected = inferSelectedRegimes(
      summary?.jurisdictions || cfg?.__jurisdictions_normalized || cfg?.jurisdictions
    );

    const inputCurrency = upper(summary?.currency_code || cfg?.currency_code || "USD");
    const revenueInput = n(summary?.annual_revenue_estimate ?? cfg?.annualTurnover, 0);
    const people = n(summary?.people_impacted_per_month ?? cfg?.userImpact, 0);

    const controlCoverage = n(
      assessment?.control_coverage_pct ??
        assessment?.compliance_score_pct ??
        summary?.control_coverage_pct ??
        summary?.compliance_score_pct,
      0,
    );
    const severityTier = n(
      assessment?.severity_tier_numeric ?? assessment?.severity_tier ?? canonicalPayload?.inputs_echo?.severityTier,
      3,
    );

    const selectedStates = cfg?.selectedStates || cfg?.states_selected || cfg?.us_states || [];
    const usStateAggMode = pickUsStateAggregationMode(cfg);

    const defaultCurrency = {
      code: inputCurrency || "USD",
      symbol: summary?.currency_symbol || cfg?.currency_symbol || "$",
    };

    const fx = canonicalPayload?.fx || null;

    const inferred = inferFederalOverlayFromConfig(cfg, summary);

    const selectedSet = new Set(selected.map(upper));
    const usContext = selectedSet.has("US_STATE") || selectedSet.has("US_FED");

    if (inferred.inferred && usContext && !selectedSet.has("US_FED")) selectedSet.add("US_FED");

    const finalRegimes = Array.from(selectedSet);

    const per_regime = {};

    finalRegimes.forEach((regime) => {
      const r = upper(regime);

      if (r === "US_STATE") {
        const currency = currencyForRegime("US_STATE", defaultCurrency);

        // Convert revenue to USD once (if FX present)
        const { revenueNative: revenueUsd, fx_status } = convertRevenueForRegime({
          revenueInput,
          inputCurrency,
          regimeCurrency: "USD",
          fx,
        });

        const base = baseRiskAmount(revenueUsd, people);
        const sevM = severityMultiplier(severityTier);
        const compM = complianceMultiplier(controlCoverage);
        const modelF = regimeModelFactor("US_STATE");

        const states = uniq((Array.isArray(selectedStates) ? selectedStates : []).map(normalizeStateKey)).filter(Boolean);
        const effectiveStates = states.length ? states : ["(BASELINE)"];

        const per_state = effectiveStates.map((stateKey) => {
          const stateModel = resolveUsStateModel(stateKey);
          const ceiling = stateModel.planning_ceiling_usd;

          const structuredWorst = Math.min(ceiling, base * sevM * compM * modelF);
          const net = structuredWorst * 0.88;
          const floor = structuredWorst * 0.65;

          return {
            state: stateKey,
            ceiling_native: Math.round(ceiling),
            worst_native: Math.round(structuredWorst),
            net_native: Math.round(net),
            floor_native: Math.round(floor),
            weight: usStateWeightForCeiling(ceiling),
            planning_basis_label: "State exposure planning estimate",
            fallback_state: Boolean(stateModel.fallback_state),
            fallback_disclosure: stateModel.fallback_disclosure,
            state_metadata: {
              legal_basis: stateModel.legal_basis,
              statutory_penalty_basis: stateModel.statutory_penalty_basis,
              confidence_level: stateModel.confidence_level,
              source_label: stateModel.source_label,
              fallback_state: Boolean(stateModel.fallback_state),
            },
          };
        });

        const agg = aggregateUsState(per_state, usStateAggMode);

        per_regime[r] = {
          regime: "US_STATE",
          enabled: true,
          statutory_ceiling_native: agg.statutory_ceiling_native,
          structured_worst_case_native: agg.structured_worst_case_native,
          net_native: agg.net_native,
          floor_native: agg.floor_native,
          currency_code: currency.code,
          currency_symbol: currency.symbol,
          display_name: "US (States)",
          model_factor: modelF,
          fx_status,
          inferred_overlay: false,
          inferred_reasons: [],
          us_state_detail: {
            aggregation_mode: usStateAggMode,
            selected_states: states,
            per_state,
          },
          fx_basis: fx
            ? {
                provider: fx?.provider || "—",
                rate_date: fx?.rate_date || "—",
                retrieved_at_utc: fx?.retrieved_at_utc || "—",
                input_currency_code: inputCurrency,
              }
            : null,
        };

        return;
      }

      const currency = currencyForRegime(r, defaultCurrency);

      const exp = computeRegimeExposure({
        regime: r,
        revenueInput,
        inputCurrency,
        regimeCurrency: currency.code,
        peoplePerMonth: people,
        complianceScorePct: controlCoverage,
        severityTier,
        fx,
      });

      const isInferredFed =
        r === "US_FED" && !selected.map(upper).includes("US_FED") && inferred.inferred && usContext;

      per_regime[r] = {
        ...exp,
        currency_code: currency.code,
        currency_symbol: currency.symbol,
        display_name:
          r === "US_FED" ? (isInferredFed ? "Inferred Federal Overlay" : "US (Federal)") : r,
        inferred_overlay: Boolean(isInferredFed),
        inferred_reasons: isInferredFed ? inferred.reasons : [],
        fx_basis: fx
          ? {
              provider: fx?.provider || "—",
              rate_date: fx?.rate_date || "—",
              retrieved_at_utc: fx?.retrieved_at_utc || "—",
              input_currency_code: inputCurrency,
            }
          : null,
      };
    });

    const combined_by_currency = {};
    Object.keys(per_regime).forEach((k) => {
      const e = per_regime[k];
      const code = e.currency_code || "USD";

      const bucket = combined_by_currency[code] || {
        currency_code: code,
        currency_symbol: e.currency_symbol || "$",
        combined_structured_worst_case_native: 0,
        combined_net_native: 0,
        combined_floor_native: 0,
        combined_statutory_ceiling_visible_native: 0,
        regimes: [],
      };

      bucket.regimes.push(k);
      bucket.combined_structured_worst_case_native += n(e.structured_worst_case_native, 0);
      bucket.combined_net_native += n(e.net_native, 0);
      bucket.combined_floor_native += n(e.floor_native, 0);
      bucket.combined_statutory_ceiling_visible_native += n(e.statutory_ceiling_native, 0);

      combined_by_currency[code] = bucket;
    });

    const headlineCode = combined_by_currency[inputCurrency]
      ? inputCurrency
      : Object.keys(combined_by_currency)[0] || inputCurrency;

    const headlineBucket = combined_by_currency[headlineCode] || {
      currency_code: inputCurrency,
      currency_symbol: defaultCurrency.symbol,
      combined_structured_worst_case_native: 0,
      combined_net_native: 0,
      combined_floor_native: 0,
      combined_statutory_ceiling_visible_native: 0,
      regimes: [],
    };

    return {
      version: "exposure.3.1",
      generated_at_iso: nowIso(),
      per_regime,
      combined_by_currency,
      headline: {
        currency_code: headlineBucket.currency_code,
        currency_symbol: headlineBucket.currency_symbol,
        combined_structured_worst_case_native: Math.round(headlineBucket.combined_structured_worst_case_native),
        combined_net_native: Math.round(headlineBucket.combined_net_native),
        combined_floor_native: Math.round(headlineBucket.combined_floor_native),
        combined_statutory_ceiling_visible_native: Math.round(
          headlineBucket.combined_statutory_ceiling_visible_native
        ),
        regimes: headlineBucket.regimes,
      },
      overlays: {
        inferred_federal_overlay: {
          inferred: Boolean(inferred.inferred && usContext),
          reasons: inferred.reasons,
        },
      },
      fx_summary: fx
        ? {
            provider: fx?.provider || "—",
            rate_date: fx?.rate_date || "—",
            retrieved_at_utc: fx?.retrieved_at_utc || "—",
            base_currency: fx?.base || fx?.base_currency || "EUR",
            input_currency_code: inputCurrency,
          }
        : null,
      us_state_aggregation: {
        mode: usStateAggMode,
        selected_states_count: Array.isArray(selectedStates) ? selectedStates.length : 0,
      },
    };
  } catch (err) {
    // Non-throwing contract: return a minimal but explicit error object
    return {
      version: "exposure.error",
      generated_at_iso: nowIso(),
      per_regime: {},
      combined_by_currency: {},
      headline: {
        currency_code: "USD",
        currency_symbol: "$",
        combined_structured_worst_case_native: 0,
        combined_net_native: 0,
        combined_floor_native: 0,
        combined_statutory_ceiling_visible_native: 0,
        regimes: [],
      },
      overlays: {},
      fx_summary: null,
      us_state_aggregation: { mode: "MAX", selected_states_count: 0 },
      __error: String(err?.message || err || "Exposure engine error"),
    };
  }
}